import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { API_BASE_URL, enterGuestMode } from '../../config/api';
import { theme } from '../../theme';
import { SkullLogo } from '../atoms/SkullLogo';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.brand.burgundy} 0%, #1a0306 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[16]};
`;

const LoginCard = styled(motion.div)`
  background: ${theme.brand.charcoal};
  border: 2px solid ${theme.brand.crimson};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[12]};
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Title = styled.h1`
  color: ${theme.brand.cream};
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.black};
  letter-spacing: ${theme.typography.letterSpacing.widest};
  text-transform: uppercase;
  margin-bottom: ${theme.spacing[6]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[8]};
`;

const Input = styled.input`
  background: ${theme.brand.burgundy};
  border: 2px solid transparent;
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[4]};
  color: ${theme.brand.cream};
  font-family: ${theme.typography.fontFamily.sans};
  font-size: 16px;

  &::placeholder {
    color: rgba(249, 242, 232, 0.6);
  }

  &:focus {
    outline: none;
    border-color: ${theme.brand.copper};
  }

  &:invalid {
    border-color: ${theme.brand.crimson};
  }
`;

const Button = styled(motion.button)<{ disabled?: boolean }>`
  background: ${props => props.disabled ? theme.brand.charcoal : theme.brand.crimson};
  color: ${theme.brand.cream};
  border: none;
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  letter-spacing: ${theme.typography.letterSpacing.wider};
  text-transform: uppercase;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const ErrorMessage = styled.div`
  color: ${theme.brand.crimson};
  font-size: 14px;
  margin-top: ${theme.spacing[2]};
`;

const ToggleLink = styled.button`
  background: none;
  border: none;
  color: ${theme.brand.copper};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  margin-top: ${theme.spacing[4]};
`;

interface LoginPageProps {
  onLogin: (user: any) => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!isLogin) {
        // Signup validation
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (formData.password.length < 12) {
          throw new Error('Password must be at least 12 characters');
        }
      }

      const endpoint = isLogin ? '/arena/login' : '/arena/signup';
      const body = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            username: formData.username,
            email: formData.email,
            password: formData.password,
            age_verified: true,
            privacy_accepted: true,
            marketing_consent: false,
            ai_processing_consent: true
          };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      if (isLogin) {
        // Store token and user info (handle both response formats)
        const token = data.access_token || data.token;
        const user = data.user;

        if (!token || !user) {
          throw new Error('Invalid response from server');
        }

        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_info', JSON.stringify(user));
        onLogin(user);
      } else {
        // After signup, switch to login
        setIsLogin(true);
        setFormData({ email: formData.email, password: '', username: '', confirmPassword: '' });
        setError('Account created! Please log in.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <Container>
      <LoginCard
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <SkullLogo size={60} />
        <Title>{isLogin ? 'Enter Arena' : 'Join Fight'}</Title>
        <p style={{ color: theme.brand.cream, opacity: 0.8, margin: 0 }}>
          {isLogin ? 'Warriors only. No cry babies.' : 'Ready to destroy your debt?'}
        </p>

        <Form onSubmit={handleSubmit}>
          {!isLogin && (
            <Input
              type="text"
              placeholder="Username"
              value={formData.username}
              onChange={handleInputChange('username')}
              required
            />
          )}

          <Input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange('email')}
            required
          />

          <Input
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleInputChange('password')}
            required
            minLength={isLogin ? undefined : 12}
          />

          {!isLogin && (
            <Input
              type="password"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              required
            />
          )}

          <Button
            type="submit"
            disabled={loading}
            whileHover={loading ? {} : { scale: 1.02 }}
            whileTap={loading ? {} : { scale: 0.98 }}
          >
            {loading ? 'Loading...' : (isLogin ? 'Enter Arena' : 'Join Fight')}
          </Button>
        </Form>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ToggleLink onClick={() => setIsLogin(!isLogin)}>
          {isLogin ? 'Need to join? Create account' : 'Already a warrior? Log in'}
        </ToggleLink>

        <div style={{
          marginTop: theme.spacing[6],
          paddingTop: theme.spacing[6],
          borderTop: `1px solid ${theme.brand.burgundy}`,
        }}>
          <Button
            type="button"
            onClick={() => {
              const guestUser = enterGuestMode();
              onLogin(guestUser);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              width: '100%',
              background: 'transparent',
              border: `2px solid ${theme.brand.copper}`,
              color: theme.brand.copper,
            }}
          >
            Continue as Guest
          </Button>
          <p style={{
            color: theme.brand.cream,
            opacity: 0.5,
            fontSize: '12px',
            marginTop: theme.spacing[2],
          }}>
            Explore the app with demo data
          </p>
        </div>
      </LoginCard>
    </Container>
  );
};